import axios from '@/lib/axios'
import { useCompanySearchStore } from '@/stores/companySearch'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { onBeforeMount, onBeforeUnmount, watch } from 'vue'

import { Company } from '@/modules/companies/interfaces/company'

interface Props {
	immediate?: boolean
	params?: Params
	queryKey?: [string]
}

interface Params {
	includes?: string[]
	only?: string[]
	corporate?: number
}

const getCompanies = async (params: any): Promise<Company[]> => {
	const { data } = await axios.get<Company[]>('api/companies/search', {
		params,
	})

	return data
}

const useCompaniesSearch = (props?: Props) => {
	const { immediate = false, params } = props || {}
	const queryClient = useQueryClient()
	const { setQuery, setCompanies, resetState } = useCompanySearchStore()
	const { query, companies } = storeToRefs(useCompanySearchStore())

	const reset = () => {
		queryClient.getQueryCache().find(['companySearch'])?.reset()
		resetState()
	}

	onBeforeMount(async () => {
		reset()
		if (immediate) {
			await refetch()
		}
	})

	onBeforeUnmount(() => {
		reset()
	})

	const {
		data,
		isLoading,
		refetch,
		isRefetching,
		isError,
		error,
		isFetching,
		isFetched,
	} = useQuery(
		['companySearch'],
		() => getCompanies({ q: query.value || undefined, ...params }),
		{
			enabled: immediate,
			retry: false,
		}
	)

	const searchCompanies = useDebounceFn(() => {
		if (query.value?.length || immediate) {
			refetch()
		} else {
			resetState()
		}
	}, 500)

	watch(query, () => {
		if (query) {
			searchCompanies()
		}
	})

	watch(
		data,
		(value) => {
			if (value) {
				setCompanies(data.value ?? [])
			}
		},
		{ immediate: true }
	)

	return {
		data,
		companies,
		isLoading,
		isError,
		error,
		isFetching,
		isFetched,

		refetch,
		isRefetching,
		setQuery,
	}
}

export default useCompaniesSearch
